import type { StyleRow } from './stylesFixtureData';
import { seasons, groups, brandOptions, suppliers, contactPersons } from './stylesFixtureData';

/**
 * Fixtures for the DL-66669 epic (dl-57695, Testmo folder 93168, part 7 of 9 — per-type
 * custom-field filter behaviour: Text, multiline Text, Multi-Label, Date). Real, persisted
 * `stylesData` rows — never `apiTransform` overrides — so every filter spec exercises the genuine
 * `applyCustomFieldFilters` path (an injected/overridden row bypasses that handler entirely —
 * DL-64371).
 *
 * Additive only: appended to `stylesData` on top of every existing generated + sibling-epic
 * fixture row. Nothing existing is edited. Ids 99100+ reserved for this epic (checked empirically
 * — the sibling DL-66657 epic reserved and used only 99000-99006).
 *
 * **Verified divergence from the epic's literal prose** (same discipline DL-66605/DL-66644/
 * DL-66657 established — read source, don't assume the Testmo/epic text):
 *  - The epic's own brief (and DL-66675's own ticket) gives the multiline Text CF's column id as
 *    `cf_{id}_description`. Checked against source (`generateColumns.ts`): `getCustomFieldColumnId`
 *    — the function that actually composes a CF filter's `id` (used for the URL param key AND
 *    every `FilterRow` testid) — branches on `CustomFieldType`/`isMultipleAllowedValue` ONLY; for
 *    `CustomFieldType.Text` it ALWAYS returns the `_text` suffix, regardless of `isMultiLine`.
 *    `isMultiLine` instead only changes `generateCustomFieldFilters`'s `type` field
 *    (`'description'` vs `'text'`) — an internal label `FilterRow.tsx`/`transforms.ts` treat
 *    completely identically to `'text'` (same `getOperatorText` branch → "contains", same
 *    `<TextInput>` control, same `getValueForUrl`/`formatForForm` handling). So a multiline Text
 *    CF's real id/URL-param/testid key is `cf_715_text` — NEVER `cf_715_description` — even though
 *    its internal `type` is `'description'`. Asserted structurally below against the REAL id, not
 *    the epic's literal (incorrect) example. This mirrors the exact shape of DL-66657's own
 *    verified Numeric-id divergence (`_number`, not `_num`).
 *  - CF 715 ("Care Instructions", `isMultiLine: true`) did not exist before this epic — no seeded
 *    definition anywhere in the mock had `isMultiLine: true`, so proving/refuting the above
 *    required adding one (`stylesCustomFieldDefinitions.ts`).
 *
 * Row groups (each a small "target + decoy" set so a filter genuinely narrows, never a single-row
 * set that would pass even with a broken predicate):
 *  - **Text family** (CF 701 "Fabric Notes", ids 99100-99102) — DL-66670/66671/66672/66673/66674.
 *    `target` + `targetSuperset` (target's value as a SUBSTRING, proving `contains`, not `=` — the
 *    literal point of DL-66671's Testmo case) + `decoy`.
 *  - **Multiline Text** (CF 715 "Care Instructions", ids 99103-99104) — DL-66675.
 *  - **Multi-Label** (CF 704 "Fabric Blend", ids 99105-99110) — DL-66676/66677/66678. The matrix
 *    DL-66677's own ticket specifies: two single-value rows (discriminate OR from AND), a
 *    both-values row, a partial-overlap row, a disjoint-value control, and a none-set control.
 *  - **Date** (CF 702 "Delivery Date", ids 99111-99117) — DL-66679/66680/66681. One 7-row set
 *    shared by both the range story (754953) and the same-day story (754954): a day-before-target
 *    guard, the From/same-day boundary, a day-after-same-day row (inside the range but OUTSIDE the
 *    same-day query — proves the two queries are genuinely distinct code paths, not one masquerading
 *    as the other), a mid-range row, the To boundary, a day-after-To guard, and a far decoy.
 */

let nextFixtureId = 99100;
const nextId = (): number => nextFixtureId++;

const baseRow = (overrides: Partial<StyleRow> & { name: string; customId: string }): StyleRow => ({
  id: nextId(),
  description: null,
  state: 'published',
  logo: '',
  season: seasons[0],
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-04-01T09:00:00',
  readyForExport: false,
  ...overrides,
});

// ---------------------------------------------------------------------------------------------
// Text family — CF 701 "Fabric Notes" (single-line Text, `cf_701_text`). Names/customIds
// prefixed "CFTEXT" so a spec can resolve rows by identity, never by index/position.
// ---------------------------------------------------------------------------------------------
export const CF_TEXT_CUSTOM_IDS = {
  target: 'CFTEXT-01',
  targetSuperset: 'CFTEXT-02',
  decoy: 'CFTEXT-03',
};

export const CF_TEXT_TARGET_VALUE = 'Recycled Poly Mesh';
// Contains the target value as a genuine SUBSTRING (not an exact equal) — the fixture that proves
// DL-66671's point: entering the FULL target value still matches under `contains`, not just `=`.
export const CF_TEXT_TARGET_SUPERSET_VALUE = 'Recycled Poly Mesh Lining';
export const CF_TEXT_DECOY_VALUE = 'Organic Cotton Twill';

// ---------------------------------------------------------------------------------------------
// Multiline Text — CF 715 "Care Instructions" (`isMultiLine: true`; real id `cf_715_text`, see
// header). Prefixed "CFDESC".
// ---------------------------------------------------------------------------------------------
export const CF_DESCRIPTION_CUSTOM_IDS = {
  target: 'CFDESC-01',
  decoy: 'CFDESC-02',
};

// The match term ("tumble dry") sits on the SECOND line — proves matching isn't limited to a
// first-line/preview slice of the multiline value.
export const CF_DESCRIPTION_TARGET_VALUE = 'Wash cold.\nTumble dry low, do not bleach.';
export const CF_DESCRIPTION_DECOY_VALUE = 'Hand wash only.\nDry flat, do not tumble.';

// ---------------------------------------------------------------------------------------------
// Multi-Label — CF 704 "Fabric Blend" (`isMultipleAllowedValue: true`, `cf_704_multiselect`).
// Allowed-value ids mirror `stylesCustomFieldDefinitions.ts`'s CF 704 options (7041 Cotton, 7042
// Polyester, 7043 Wool). Prefixed "CFML". Names below map onto the ticket's own OR-vs-AND matrix:
// cottonOnly/polyesterOnly are the discriminating single-value rows, cottonAndPolyester proves the
// two-selected-values union, cottonAndWool is the partial-overlap row (only "cotton" of the two
// selected values is present), woolOnly is the disjoint control (never matches a Cotton/Polyester
// selection), noneSet has no CF 704 entry at all (null-handling control).
// ---------------------------------------------------------------------------------------------
export const CF_MULTILABEL_CUSTOM_IDS = {
  cottonOnly: 'CFML-01',
  polyesterOnly: 'CFML-02',
  cottonAndPolyester: 'CFML-03',
  woolOnly: 'CFML-04',
  noneSet: 'CFML-05',
  cottonAndWool: 'CFML-06',
};

export const CF_MULTILABEL_ALLOWED_VALUE_IDS = {
  cotton: 7041,
  polyester: 7042,
  wool: 7043,
};

// ---------------------------------------------------------------------------------------------
// Date — CF 702 "Delivery Date" (`cf_702_date`, `dateRange` filter type). Prefixed "CFDATE". See
// header for the shared-fixture rationale across the range (754953) and same-day (754954) stories.
// ---------------------------------------------------------------------------------------------
export const CF_DATE_CUSTOM_IDS = {
  dayBeforeTarget: 'CFDATE-01', // 2026-08-09 — excluded from both range and same-day queries
  target: 'CFDATE-02', // 2026-08-10 — the From/same-day boundary; included in BOTH queries
  dayAfterTarget: 'CFDATE-03', // 2026-08-11 — inside the RANGE but outside the SAME-DAY query
  midRange: 'CFDATE-04', // 2026-08-15 — included in the range only
  toBoundary: 'CFDATE-05', // 2026-08-20 — the To boundary; included in the range only
  dayAfterTo: 'CFDATE-06', // 2026-08-21 — excluded from both
  farDecoy: 'CFDATE-07', // 2026-01-01 — excluded from both, unrelated month
};

export const CF_DATE_VALUES = {
  dayBeforeTarget: '2026-08-09',
  target: '2026-08-10',
  dayAfterTarget: '2026-08-11',
  midRange: '2026-08-15',
  toBoundary: '2026-08-20',
  dayAfterTo: '2026-08-21',
  farDecoy: '2026-01-01',
};

export const buildCfFilterTypesFixtureStyles = (): StyleRow[] => [
  baseRow({
    name: 'CF Type Contract Text Target',
    customId: CF_TEXT_CUSTOM_IDS.target,
    customFields: [{ id: 701, type: 'text', value: { id: 0, value: CF_TEXT_TARGET_VALUE } }],
  }),
  baseRow({
    name: 'CF Type Contract Text Target Superset',
    customId: CF_TEXT_CUSTOM_IDS.targetSuperset,
    customFields: [
      { id: 701, type: 'text', value: { id: 0, value: CF_TEXT_TARGET_SUPERSET_VALUE } },
    ],
  }),
  baseRow({
    name: 'CF Type Contract Text Decoy',
    customId: CF_TEXT_CUSTOM_IDS.decoy,
    customFields: [{ id: 701, type: 'text', value: { id: 0, value: CF_TEXT_DECOY_VALUE } }],
  }),

  baseRow({
    name: 'CF Type Contract Description Target',
    customId: CF_DESCRIPTION_CUSTOM_IDS.target,
    customFields: [{ id: 715, type: 'text', value: { id: 0, value: CF_DESCRIPTION_TARGET_VALUE } }],
  }),
  baseRow({
    name: 'CF Type Contract Description Decoy',
    customId: CF_DESCRIPTION_CUSTOM_IDS.decoy,
    customFields: [{ id: 715, type: 'text', value: { id: 0, value: CF_DESCRIPTION_DECOY_VALUE } }],
  }),

  baseRow({
    name: 'CF Type Contract Cotton Only',
    customId: CF_MULTILABEL_CUSTOM_IDS.cottonOnly,
    customFields: [
      {
        id: 704,
        type: 'allowedvalue',
        values: [{ id: CF_MULTILABEL_ALLOWED_VALUE_IDS.cotton, value: 'Cotton' }],
      },
    ],
  }),
  baseRow({
    name: 'CF Type Contract Polyester Only',
    customId: CF_MULTILABEL_CUSTOM_IDS.polyesterOnly,
    customFields: [
      {
        id: 704,
        type: 'allowedvalue',
        values: [{ id: CF_MULTILABEL_ALLOWED_VALUE_IDS.polyester, value: 'Polyester' }],
      },
    ],
  }),
  baseRow({
    name: 'CF Type Contract Cotton And Polyester',
    customId: CF_MULTILABEL_CUSTOM_IDS.cottonAndPolyester,
    customFields: [
      {
        id: 704,
        type: 'allowedvalue',
        values: [
          { id: CF_MULTILABEL_ALLOWED_VALUE_IDS.cotton, value: 'Cotton' },
          { id: CF_MULTILABEL_ALLOWED_VALUE_IDS.polyester, value: 'Polyester' },
        ],
      },
    ],
  }),
  baseRow({
    name: 'CF Type Contract Wool Only',
    customId: CF_MULTILABEL_CUSTOM_IDS.woolOnly,
    customFields: [
      {
        id: 704,
        type: 'allowedvalue',
        values: [{ id: CF_MULTILABEL_ALLOWED_VALUE_IDS.wool, value: 'Wool' }],
      },
    ],
  }),
  baseRow({
    name: 'CF Type Contract None Set',
    customId: CF_MULTILABEL_CUSTOM_IDS.noneSet,
    customFields: [],
  }),
  baseRow({
    name: 'CF Type Contract Cotton And Wool',
    customId: CF_MULTILABEL_CUSTOM_IDS.cottonAndWool,
    customFields: [
      {
        id: 704,
        type: 'allowedvalue',
        values: [
          { id: CF_MULTILABEL_ALLOWED_VALUE_IDS.cotton, value: 'Cotton' },
          { id: CF_MULTILABEL_ALLOWED_VALUE_IDS.wool, value: 'Wool' },
        ],
      },
    ],
  }),

  baseRow({
    name: 'CF Type Contract Date Day Before Target',
    customId: CF_DATE_CUSTOM_IDS.dayBeforeTarget,
    customFields: [
      { id: 702, type: 'date', value: { id: 0, value: CF_DATE_VALUES.dayBeforeTarget } },
    ],
  }),
  baseRow({
    name: 'CF Type Contract Date Target',
    customId: CF_DATE_CUSTOM_IDS.target,
    customFields: [{ id: 702, type: 'date', value: { id: 0, value: CF_DATE_VALUES.target } }],
  }),
  baseRow({
    name: 'CF Type Contract Date Day After Target',
    customId: CF_DATE_CUSTOM_IDS.dayAfterTarget,
    customFields: [
      { id: 702, type: 'date', value: { id: 0, value: CF_DATE_VALUES.dayAfterTarget } },
    ],
  }),
  baseRow({
    name: 'CF Type Contract Date Mid Range',
    customId: CF_DATE_CUSTOM_IDS.midRange,
    customFields: [{ id: 702, type: 'date', value: { id: 0, value: CF_DATE_VALUES.midRange } }],
  }),
  baseRow({
    name: 'CF Type Contract Date To Boundary',
    customId: CF_DATE_CUSTOM_IDS.toBoundary,
    customFields: [{ id: 702, type: 'date', value: { id: 0, value: CF_DATE_VALUES.toBoundary } }],
  }),
  baseRow({
    name: 'CF Type Contract Date Day After To',
    customId: CF_DATE_CUSTOM_IDS.dayAfterTo,
    customFields: [{ id: 702, type: 'date', value: { id: 0, value: CF_DATE_VALUES.dayAfterTo } }],
  }),
  baseRow({
    name: 'CF Type Contract Date Far Decoy',
    customId: CF_DATE_CUSTOM_IDS.farDecoy,
    customFields: [{ id: 702, type: 'date', value: { id: 0, value: CF_DATE_VALUES.farDecoy } }],
  }),
];
